// src/pages/FleetManagementPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Alert,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Tooltip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Switch,
  FormControlLabel,
  LinearProgress,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelIcon,
  Build as MaintenanceIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  QrCode as QrCodeIcon,
  Engineering as EngineeringIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { deviceAPI, vehicleAPI } from '../../services/api';
import api from '../../services/api';



const MobileVehicleCard: React.FC<{
  vehicle: FleetVehicle;
  onEdit: (vehicle: FleetVehicle) => void;
  onBindDevice: (vehicle: FleetVehicle) => void;
  onUnbindDevice: (vehicle: FleetVehicle) => void;
  getCategoryColor: (category: string) => string;
  getCategoryIcon: (category: string) => React.ReactNode;
  getDepartmentName: (value: string) => string;
  getRegionName: (value: string) => string;
  drivers: any[];
}> = ({ vehicle, onEdit, onBindDevice, onUnbindDevice, getCategoryColor, getCategoryIcon, getDepartmentName, getRegionName, drivers }) => (
  <Card variant="outlined">
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 0 }}>
          <Avatar sx={{ bgcolor: getCategoryColor(vehicle.category) + '.main', width: 32, height: 32 }}>
            {getCategoryIcon(vehicle.category)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {vehicle.plateNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {vehicle.make} {vehicle.model} ({vehicle.year})
            </Typography>
          </Box>
        </Box>
        <Chip
          size="small"
          label={vehicle.isActive ? 'Active' : 'Inactive'}
          color={vehicle.isActive ? 'success' : 'error'}
          sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Details */}
      <Stack spacing={0.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Category:</Typography>
          <Chip
            size="small"
            label={vehicle.category.replace('_', ' ')}
            color={getCategoryColor(vehicle.category) as any}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Device:</Typography>
          <Chip
            size="small"
            label={vehicle.deviceBound ? 'Bound' : 'Unbound'}
            color={vehicle.deviceBound ? 'success' : 'error'}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Box>

        {vehicle.assignedDepartment && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Dept:</Typography>
            <Typography variant="caption" fontWeight="500" noWrap sx={{ maxWidth: '60%', textAlign: 'right' }}>
              {getDepartmentName(vehicle.assignedDepartment)}
            </Typography>
          </Box>
        )}

        {vehicle.assignedRegion && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Region:</Typography>
            <Typography variant="caption" fontWeight="500" noWrap sx={{ maxWidth: '60%', textAlign: 'right' }}>
              {getRegionName(vehicle.assignedRegion)}
            </Typography>
          </Box>
        )}

        {vehicle.assignedDriver && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Driver:</Typography>
            <Typography variant="caption" fontWeight="500" noWrap sx={{ maxWidth: '60%', textAlign: 'right' }}>
              {(() => {
                const driver = drivers.find(d => d._id === vehicle.assignedDriver);
                return driver ? `${driver.firstName} ${driver.lastName}` : 'Assigned';
              })()}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider', justifyContent: 'center' }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(vehicle)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {vehicle.deviceBound ? (
          <Tooltip title="Unbind">
            <IconButton size="small" color="error" onClick={() => onUnbindDevice(vehicle)}>
              <UnlinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Bind">
            <IconButton size="small" color="primary" onClick={() => onBindDevice(vehicle)}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="View">
          <IconButton size="small">
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </CardContent>
  </Card>
);




interface FleetVehicle {
  id: string;
  vin: string;
  plateNumber: string;
  category: 'executive' | 'staff_bus' | 'technical' | 'general';
  make: string;
  model: string;
  year: number;
  color: string;
  fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid';
  assignedDepartment?: string;
  assignedRegion?: string;
  assignedDriver?: string;
  deviceId?: string;
  deviceBound: boolean;
  isActive: boolean;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  insuranceExpiry?: Date;
  registrationExpiry?: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface GPSDevice {
  id: string;
  imei: string;
  vin?: string;
  model: string;
  manufacturer: string;
  status: 'active' | 'inactive' | 'bound' | 'unbound';
  boundVehicleId?: string;
  lastSeen?: Date;
}

interface VehicleCategory {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  vehicleCount: number;
  boundCount: number;
}

const FleetManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [devices, setDevices] = useState<GPSDevice[]>([]);
  const [openAddVehicle, setOpenAddVehicle] = useState(false);
  const [openBindDevice, setOpenBindDevice] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<FleetVehicle | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openUnbindDialog, setOpenUnbindDialog] = useState(false);
  const [selectedUnbindVehicle, setSelectedUnbindVehicle] = useState<FleetVehicle | null>(null);
  const [unbindReason, setUnbindReason] = useState<string>('manual_unbind');
  const [suggestedDevice, setSuggestedDevice] = useState<GPSDevice | null>(null);


  // Form state with isActive property
  const [formData, setFormData] = useState<{
    plateNumber: string;
    category: 'executive' | 'staff_bus' | 'technical' | 'general' | '';
    make: string;
    model: string;
    year: number;
    color: string;
    fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid' | '';
    assignedDepartment: string;
    assignedRegion: string;
    assignedDriver: string;
    isActive: boolean;
    
    lastMaintenanceDate: string;
    nextMaintenanceDate: string;
    maintenanceIntervalKm: number;
    currentMileage: number;
    serviceProvider: string;
  }>({
    plateNumber: '',
    category: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    fuelType: '',
    assignedDepartment: '',
    assignedRegion: '',
    assignedDriver: '',
    isActive: true,
    // ✅ ADD THESE:
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    maintenanceIntervalKm: 0,
    currentMileage: 0,
    serviceProvider: '',
  });

  // Load data from API
  useEffect(() => {
    loadVehicles();
    loadDevices();
    loadDrivers();
  }, []);

  // Auto-suggest device when bind dialog opens based on VIN matching
  useEffect(() => {
    if (openBindDevice && selectedVehicle) {
      const matchingDevice = findDeviceByVIN(selectedVehicle.vin);
      setSuggestedDevice(matchingDevice);
    } else {
      setSuggestedDevice(null);
    }
  }, [openBindDevice, selectedVehicle, devices]);

  const loadDrivers = async () => {
    try {
      const response = await api.get('/drivers'); // Adjust endpoint if needed
      setDrivers(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading drivers:', error);
      setError('Failed to load drivers. Please check your connection.');
    }
  };


  const findDeviceByVIN = (vehicleVIN: string): GPSDevice | null => {
    if (!vehicleVIN) return null;

    // Find device with matching VIN in the devices list
    const matchingDevice = devices.find(device => {
      // Device must be unbound
      if (device.status !== 'unbound') return false;

      // Check if device has a VIN property (you'll need to add this to device data)
      // For now, we'll use the device ID/IMEI matching logic
      return device.imei === vehicleVIN; // This will be enhanced when devices have VIN
    });

    return matchingDevice || null;
  };

  // Constants for departments and regions
  const DEPARTMENTS = [
    { value: 'operations', label: 'Operations' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'security', label: 'Security' },
    { value: 'administration', label: 'Administration' },
    { value: 'executive', label: 'Executive' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'it', label: 'Information Technology' },
    { value: 'procurement', label: 'Procurement' },
    { value: 'field_operations', label: 'Field Operations' },
    { value: 'customer_service', label: 'Customer Service' },
  ];

  const REGIONS = [
    { value: 'lagos_mainland', label: 'Lagos Mainland' },
    { value: 'lagos_island', label: 'Lagos Island' },
    { value: 'victoria_island', label: 'Victoria Island' },
    { value: 'ikeja', label: 'Ikeja' },
    { value: 'lekki', label: 'Lekki' },
    { value: 'ikorodu', label: 'Ikorodu' },
    { value: 'badagry', label: 'Badagry' },
    { value: 'epe', label: 'Epe' },
    { value: 'alimosho', label: 'Alimosho' },
    { value: 'agege', label: 'Agege' },
    { value: 'kosofe', label: 'Kosofe' },
    { value: 'mushin', label: 'Mushin' },
    { value: 'oshodi_isolo', label: 'Oshodi-Isolo' },
    { value: 'surulere', label: 'Surulere' },
    { value: 'other', label: 'Other Location' },
  ];

  // Helper functions for display names
  const getDepartmentName = (value: string) => {
    const dept = DEPARTMENTS.find(d => d.value === value);
    return dept ? dept.label : value;
  };

  const getRegionName = (value: string) => {
    const region = REGIONS.find(r => r.value === value);
    return region ? region.label : value;
  };



  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('📡 Calling vehicleAPI.getAllVehicles()...');
      const response = await vehicleAPI.getAllVehicles();

      // ✅ LOG THE RAW RESPONSE
      console.log('📦 Raw API Response:', response);
      console.log('📊 Response type:', typeof response);
      console.log('📊 Is array?', Array.isArray(response));
      console.log('📊 Response length:', response?.length);

      if (response && response.length > 0) {
        console.log('📦 First vehicle:', response[0]);
      }

      // ✅ ADD SAFETY CHECK
      if (!response || !Array.isArray(response)) {
        console.error('❌ Invalid response format:', response);
        setError('Invalid data format received from server');
        return;
      }

      const transformedVehicles = response.map((vehicle: any) => {
        console.log('🔄 Transforming vehicle:', vehicle._id || vehicle.id);

        return {
          id: vehicle._id || vehicle.id,
          vin: vehicle.vin,
          plateNumber: vehicle.plateNumber,
          category: vehicle.category,
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          color: vehicle.color || '',
          fuelType: vehicle.fuelType,
          assignedDepartment: vehicle.assignment?.currentDepartment || '',
          assignedRegion: vehicle.assignment?.currentRegion || '',
          assignedDriver: vehicle.assignment?.currentDriver || '',
          deviceId: (() => {
            const deviceBinding = vehicle.deviceBinding?.currentDeviceId;
            if (!deviceBinding) return undefined;

            if (typeof deviceBinding === 'string') return deviceBinding;

            if (typeof deviceBinding === 'object' && deviceBinding._id) {
              return deviceBinding._id;
            }

            return undefined;
          })(),
          deviceBound: vehicle.deviceBinding?.isDeviceBound || false,
          isActive: vehicle.isActive !== false,
          lastMaintenanceDate: vehicle.maintenance?.lastServiceDate ? new Date(vehicle.maintenance.lastServiceDate) : undefined,
          nextMaintenanceDate: vehicle.maintenance?.nextServiceDate ? new Date(vehicle.maintenance.nextServiceDate) : undefined,
          insuranceExpiry: vehicle.documentation?.insuranceExpiry ? new Date(vehicle.documentation.insuranceExpiry) : undefined,
          registrationExpiry: vehicle.documentation?.registrationExpiry ? new Date(vehicle.documentation.registrationExpiry) : undefined,
          createdAt: new Date(vehicle.createdAt || Date.now()),
          updatedAt: new Date(vehicle.updatedAt || Date.now()),
        };
      });

      console.log('✅ Transformed vehicles:', transformedVehicles.length);
      console.log('📦 First transformed vehicle:', transformedVehicles[0]);

      setVehicles(transformedVehicles);
    } catch (error) {
      console.error('❌ Error loading vehicles:', error);
      setError('Failed to load vehicles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await deviceAPI.getAllDevices();
  
      // Transform API response with VIN support
      const transformedDevices = response.map((device: any) => {
        // ✅ FIX: Extract vehicle ID properly
        let vehicleId = null;
        if (device.vehicleId) {
          // If vehicleId is an object with _id
          if (typeof device.vehicleId === 'object' && device.vehicleId._id) {
            vehicleId = device.vehicleId._id;
          } else if (typeof device.vehicleId === 'string') {
            vehicleId = device.vehicleId;
          }
        }
  
        return {
          id: device._id || device.id,
          imei: device.imei,
          vin: device.vin || null,
          model: device.model || device.make || 'GPS Device',
          manufacturer: device.manufacturer || device.deviceType || 'Teltonika',
          status: (() => {
            if (device.isBoundToVehicle === true && vehicleId) {
              return 'bound';
            }
            if (device.status === 'active' || device.status === 'online') {
              return 'unbound';
            }
            return 'unbound';
          })() as 'active' | 'inactive' | 'bound' | 'unbound',
          boundVehicleId: vehicleId,  // ✅ Now correctly extracted
          lastSeen: device.lastSeen ? new Date(device.lastSeen) : undefined,
        };
      });
  
      console.log('🔍 Loaded devices:', transformedDevices);
      console.log('🔍 Devices with bound vehicles:', transformedDevices.filter(d => d.status === 'bound'));
      setDevices(transformedDevices);
    } catch (error) {
      console.error('Error loading devices:', error);
      setError('Failed to load GPS devices. Please check your connection.');
    }
  };

  const handleAddVehicle = async () => {
    if (!formData.plateNumber || !formData.category || !formData.fuelType) {
      setError('Please fill in all required fields');
      return;
    }
  
    try {
      setLoading(true);
      setError('');
  
      const vehicleData: any = {
        plateNumber: formData.plateNumber,
        category: formData.category,
        make: formData.make,
        model: formData.model,
        year: formData.year,
        color: formData.color || undefined,
        fuelType: formData.fuelType,
        isActive: formData.isActive,
      };
  
      if (formData.assignedDepartment || formData.assignedRegion || formData.assignedDriver) {
        vehicleData.assignment = {
          currentDepartment: formData.assignedDepartment || undefined,
          currentRegion: formData.assignedRegion || undefined,
          currentDriver: formData.assignedDriver || undefined,
        };
      }
  
      // ✅ ADD THIS: Include maintenance data
      if (formData.lastMaintenanceDate || formData.nextMaintenanceDate || 
          formData.maintenanceIntervalKm || formData.currentMileage) {
        vehicleData.maintenance = {
          lastServiceDate: formData.lastMaintenanceDate || undefined,
          nextServiceDate: formData.nextMaintenanceDate || undefined,
          serviceIntervalKm: formData.maintenanceIntervalKm || undefined,
          currentMileageKm: formData.currentMileage || undefined,
          serviceProvider: formData.serviceProvider || undefined,
        };
      }
  
      console.log('Sending vehicle data:', vehicleData);
  
      if (editMode && selectedVehicle) {
        await vehicleAPI.updateVehicle(selectedVehicle.id, vehicleData);
        setSuccess('Vehicle updated successfully');
      } else {
        await vehicleAPI.createVehicle(vehicleData);
        setSuccess('Vehicle added successfully');
      }
  
      await loadVehicles();
  
      setOpenAddVehicle(false);
      setEditMode(false);
      setSelectedVehicle(null);
      resetForm();
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save vehicle';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      plateNumber: '',
      category: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      fuelType: '',
      assignedDepartment: '',
      assignedRegion: '',
      assignedDriver: '',
      isActive: true,
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    maintenanceIntervalKm: 0,
    currentMileage: 0,
    serviceProvider: '',
    });
  };

  const handleEditVehicle = (vehicle: FleetVehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      plateNumber: vehicle.plateNumber,
      category: vehicle.category,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      color: vehicle.color || '',
      fuelType: vehicle.fuelType,
      assignedDepartment: vehicle.assignedDepartment || '',
      assignedRegion: vehicle.assignedRegion || '',
      assignedDriver: vehicle.assignedDriver || '',
      isActive: vehicle.isActive,
      // ✅ ADD THESE:
      lastMaintenanceDate: vehicle.lastMaintenanceDate 
        ? new Date(vehicle.lastMaintenanceDate).toISOString().split('T')[0] 
        : '',
      nextMaintenanceDate: vehicle.nextMaintenanceDate 
        ? new Date(vehicle.nextMaintenanceDate).toISOString().split('T')[0] 
        : '',
      maintenanceIntervalKm: 0, // Load from vehicle if you store it
      currentMileage: 0, // Load from vehicle if you store it
      serviceProvider: '', // Load from vehicle if you store it
    });
    setEditMode(true);
    setOpenAddVehicle(true);
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }

    try {
      setLoading(true);
      await vehicleAPI.deleteVehicle(vehicleId);
      setSuccess('Vehicle deleted successfully');
      await loadVehicles();
    } catch (error: any) {
      console.error('Error deleting vehicle:', error);
      setError('Failed to delete vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBindDevice = async (vehicleId: string, deviceId: string, forceSwitch: boolean = false) => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post(`/vehicles/${vehicleId}/bind-device`, {
        deviceId,
        forceSwitch
      });

      setSuccess('Device bound successfully');
      setOpenBindDevice(false);

      // ✅ Clear state before reloading
      setDevices([]);
      setVehicles([]);

      await Promise.all([loadVehicles(), loadDevices()]);
    } catch (error: any) {
      console.error('Error binding device:', error);

      if (error.response?.status === 409 && error.response?.data?.requiresConfirmation) {
        const confirmed = window.confirm(
          `${error.response.data.message}\n\n` +
          `Current: ${error.response.data.currentDevice?.imei}\n` +
          `New: ${error.response.data.newDevice?.imei}`
        );

        if (confirmed) {
          await handleBindDevice(vehicleId, deviceId, true);
        }
        return;
      }

      const errorMessage = error.response?.data?.message || 'Failed to bind device. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUnbindDevice = async (vehicleId: string, reason: string = 'manual_unbind') => {
    try {
      setLoading(true);
      setError('');

      await api.post(`/vehicles/${vehicleId}/unbind-device`, { reason });


      // ✅ Delete vehicle after unbinding
      await vehicleAPI.deleteVehicle(vehicleId);

      setSuccess('Device unbound and vehicle removed from system');
      setOpenUnbindDialog(false);
      setSelectedUnbindVehicle(null);
      setUnbindReason('manual_unbind');

      // ✅ Clear state before reloading to force fresh render
      setDevices([]);
      setVehicles([]);

      await Promise.all([loadVehicles(), loadDevices()]);
    } catch (error: any) {
      console.error('Error unbinding device:', error);

      if (error.response?.status === 400 && error.response?.data?.message?.includes('enum')) {
        setError('Invalid unbind reason selected. Please try a different reason.');
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to unbind device. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to open the unbind dialog
  const openUnbindDialogForVehicle = (vehicle: FleetVehicle) => {
    setSelectedUnbindVehicle(vehicle);
    setOpenUnbindDialog(true);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'executive': return 'primary';
      case 'staff_bus': return 'secondary';
      case 'technical': return 'warning';
      case 'general': return 'info';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'executive': return <PersonIcon />;
      case 'staff_bus': return <BusinessIcon />;
      case 'technical': return <EngineeringIcon />;
      case 'general': return <CarIcon />;
      default: return <CarIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'bound': return 'success';
      case 'inactive':
      case 'unbound': return 'error';
      default: return 'default';
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    // ✅ Enhanced search including IMEI from bound device
    const matchesSearch = (() => {
      if (!searchQuery) return true;

      const searchLower = searchQuery.toLowerCase();

      // Search in vehicle fields
      const vehicleMatch =
        vehicle.plateNumber?.toLowerCase().includes(searchLower) ||
        vehicle.vin?.toLowerCase().includes(searchLower) ||
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.year?.toString().includes(searchLower);

      // ✅ Search in device IMEI if device is bound
      if (vehicle.deviceBound && vehicle.deviceId) {
        const device = devices.find(d => d.id === vehicle.deviceId);
        if (device && device.imei?.toLowerCase().includes(searchLower)) {
          return true;
        }
      }

      return vehicleMatch;
    })();

    const matchesCategory = selectedCategory === '' || vehicle.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const unboundDevices = devices.filter(d => d.status === 'unbound');

  const categoryStats: VehicleCategory[] = [
    {
      key: 'executive',
      name: 'Executive Pool',
      description: 'High-end vehicles for executive use and official meetings',
      icon: <PersonIcon />,
      color: 'primary',
      vehicleCount: vehicles.filter(v => v.category === 'executive').length,
      boundCount: vehicles.filter(v => v.category === 'executive' && v.deviceBound).length,
    },
    {
      key: 'staff_bus',
      name: 'Staff Bus',
      description: 'Buses and larger vehicles for staff transportation',
      icon: <BusinessIcon />,
      color: 'secondary',
      vehicleCount: vehicles.filter(v => v.category === 'staff_bus').length,
      boundCount: vehicles.filter(v => v.category === 'staff_bus' && v.deviceBound).length,
    },
    {
      key: 'technical',
      name: 'Technical Units',
      description: 'Specialized vehicles for technical and field operations',
      icon: <EngineeringIcon />,
      color: 'warning',
      vehicleCount: vehicles.filter(v => v.category === 'technical').length,
      boundCount: vehicles.filter(v => v.category === 'technical' && v.deviceBound).length,
    },
    {
      key: 'general',
      name: 'General Pool',
      description: 'General purpose vehicles for various operational needs',
      icon: <CarIcon />,
      color: 'info',
      vehicleCount: vehicles.filter(v => v.category === 'general').length,
      boundCount: vehicles.filter(v => v.category === 'general' && v.deviceBound).length,
    },
  ];

  const upcomingMaintenance = vehicles.filter(vehicle => {
    if (!vehicle.nextMaintenanceDate) return false;
    const daysUntil = Math.ceil((vehicle.nextMaintenanceDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil > 0;
  });

  const overdueMaintenance = vehicles.filter(vehicle => {
    if (!vehicle.nextMaintenanceDate) return false;
    return vehicle.nextMaintenanceDate.getTime() < Date.now();
  });

  const renderVehicleForm = () => (
    <Dialog
      open={openAddVehicle}
      onClose={() => {
        setOpenAddVehicle(false);
        setEditMode(false);
        setSelectedVehicle(null);
        resetForm();
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <CarIcon />
            </Avatar>
            <Typography variant={isMobile ? 'h6' : 'h5'}>
              {editMode ? 'Edit Vehicle' : 'Add New Vehicle'}
            </Typography>
          </Box>
          {isMobile && (
            <IconButton onClick={() => {
              setOpenAddVehicle(false);
              setEditMode(false);
              setSelectedVehicle(null);
              resetForm();
            }} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>

          {/* Basic Vehicle Information */}
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CarIcon />
              Vehicle Information
            </Typography>
            <Grid container spacing={2}>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Plate Number"
                  value={formData.plateNumber}
                  onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vehicle Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    label="Vehicle Category"
                  >
                    <MenuItem value="executive">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        Executive Pool
                      </Box>
                    </MenuItem>
                    <MenuItem value="staff_bus">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        Staff Bus
                      </Box>
                    </MenuItem>
                    <MenuItem value="technical">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EngineeringIcon />
                        Technical Units
                      </Box>
                    </MenuItem>
                    <MenuItem value="general">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CarIcon />
                        General Pool
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Fuel Type</InputLabel>
                  <Select
                    value={formData.fuelType}
                    onChange={(e) => setFormData({ ...formData, fuelType: e.target.value as any })}
                    label="Fuel Type"
                  >
                    <MenuItem value="petrol">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FuelIcon />
                        Petrol
                      </Box>
                    </MenuItem>
                    <MenuItem value="diesel">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FuelIcon />
                        Diesel
                      </Box>
                    </MenuItem>
                    <MenuItem value="electric">Electric</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Make"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Year"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </Grid>
            </Grid>
          </Card>

          {/* Enhanced Assignment Section */}
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon />
              Assignment & Allocation
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Assign this vehicle to specific departments, regions, and drivers for better fleet organization and tracking.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={formData.assignedDepartment}
                    onChange={(e) => setFormData({ ...formData, assignedDepartment: e.target.value })}
                    label="Department"
                  >
                    <MenuItem value="">
                      <em>Not Assigned</em>
                    </MenuItem>
                    {DEPARTMENTS.map((dept) => (
                      <MenuItem key={dept.value} value={dept.value}>
                        {dept.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Region/Location</InputLabel>
                  <Select
                    value={formData.assignedRegion}
                    onChange={(e) => setFormData({ ...formData, assignedRegion: e.target.value })}
                    label="Region/Location"
                  >
                    <MenuItem value="">
                      <em>Not Assigned</em>
                    </MenuItem>
                    {REGIONS.map((region) => (
                      <MenuItem key={region.value} value={region.value}>
                        {region.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Assigned Driver</InputLabel>
                  <Select
                    value={formData.assignedDriver}
                    onChange={(e) => setFormData({ ...formData, assignedDriver: e.target.value })}
                    label="Assigned Driver"
                  >
                    <MenuItem value="">
                      <em>Not Assigned</em>
                    </MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver._id} value={driver._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          {driver.firstName} {driver.lastName}
                          {driver.licenseNumber && (
                            <Typography variant="caption" color="text.secondary">
                              (License: {driver.licenseNumber})
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Assignment Summary */}
            {(formData.assignedDepartment || formData.assignedRegion || formData.assignedDriver) && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Assignment Summary:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.assignedDepartment && (
                    <Chip
                      size="small"
                      icon={<BusinessIcon />}
                      label={`Dept: ${DEPARTMENTS.find(d => d.value === formData.assignedDepartment)?.label}`}
                      color="primary"
                    />
                  )}
                  {formData.assignedRegion && (
                    <Chip
                      size="small"
                      icon={<LocationIcon />}
                      label={`Region: ${REGIONS.find(r => r.value === formData.assignedRegion)?.label}`}
                      color="secondary"
                    />
                  )}
                  {formData.assignedDriver && (
                    <Chip
                      size="small"
                      icon={<PersonIcon />}
                      label={`Driver: ${formData.assignedDriver}`}
                      color="success"
                    />
                  )}
                </Stack>
              </Box>
            )}

            {/* Category-specific Guidance */}
            {formData.category && (
              <Box sx={{ mt: 2 }}>
                {formData.category === 'executive' && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Executive Pool:</strong> Consider assigning to Administration or Executive departments.
                      These vehicles typically require higher maintenance standards.
                    </Typography>
                  </Alert>
                )}
                {formData.category === 'staff_bus' && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Staff Bus:</strong> Usually assigned to HR or Operations departments.
                      Consider route-based regional assignments.
                    </Typography>
                  </Alert>
                )}
                {formData.category === 'technical' && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Technical Units:</strong> Typically assigned to Field Operations, Maintenance, or IT departments.
                      Regional assignment is important for service coverage.
                    </Typography>
                  </Alert>
                )}
                {formData.category === 'general' && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>General Pool:</strong> Flexible assignment to any department based on current needs.
                      Can be reassigned as requirements change.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Card>

          {/* Usage Preferences (Optional) */}
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon />
              Additional Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active in Fleet"
                />
                <Typography variant="body2" color="text.secondary">
                  Inactive vehicles won't appear in assignment lists
                </Typography>
              </Grid>
            </Grid>
          </Card>


          {/* Maintenance Information */}
<Card sx={{ p: 2 }}>
  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <MaintenanceIcon />
    Maintenance Schedule
  </Typography>

  <Alert severity="info" sx={{ mb: 2 }}>
    Set maintenance schedules to receive alerts before service is due
  </Alert>

  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        type="date"
        label="Last Service Date"
        value={formData.lastMaintenanceDate || ''}
        onChange={(e) => setFormData({ ...formData, lastMaintenanceDate: e.target.value })}
        InputLabelProps={{ shrink: true }}
        helperText="When was the last maintenance performed?"
      />
    </Grid>

    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        type="date"
        label="Next Service Due"
        value={formData.nextMaintenanceDate || ''}
        onChange={(e) => setFormData({ ...formData, nextMaintenanceDate: e.target.value })}
        InputLabelProps={{ shrink: true }}
        helperText="When is the next maintenance due?"
      />
    </Grid>

    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        type="number"
        label="Service Interval (km)"
        value={formData.maintenanceIntervalKm || ''}
        onChange={(e) => setFormData({ ...formData, maintenanceIntervalKm: parseInt(e.target.value) || 0 })}
        helperText="e.g., 5000 km intervals"
      />
    </Grid>

    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        type="number"
        label="Current Mileage (km)"
        value={formData.currentMileage || ''}
        onChange={(e) => setFormData({ ...formData, currentMileage: parseInt(e.target.value) || 0 })}
        helperText="Current odometer reading"
      />
    </Grid>

    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        type="text"
        label="Service Provider"
        value={formData.serviceProvider || ''}
        onChange={(e) => setFormData({ ...formData, serviceProvider: e.target.value })}
        helperText="e.g., Toyota Service Center"
      />
    </Grid>
  </Grid>

  {/* Maintenance Calculation Helper */}
  {formData.maintenanceIntervalKm && formData.currentMileage && formData.nextMaintenanceDate && (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        📊 Maintenance Estimate:
      </Typography>
      <Typography variant="body2">
        Next service due at: <strong>{formData.currentMileage + formData.maintenanceIntervalKm} km</strong>
      </Typography>
      <Typography variant="body2">
        Current mileage: <strong>{formData.currentMileage} km</strong>
      </Typography>
      <Typography variant="body2">
        Remaining: <strong>{formData.maintenanceIntervalKm} km</strong>
      </Typography>
    </Box>
  )}
</Card>

        </Box>
      </DialogContent>
      <DialogActions sx={{
        flexDirection: isMobile ? 'column-reverse' : 'row',
        gap: isMobile ? 1 : 0,
        p: 2,
        '& > button': {
          width: isMobile ? '100%' : 'auto'
        }
      }}>
        <Button
          onClick={() => {
            setOpenAddVehicle(false);
            setEditMode(false);
            setSelectedVehicle(null);
            resetForm();
          }}
          fullWidth={isMobile}
          variant={isMobile ? 'outlined' : 'text'}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddVehicle}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : (editMode ? <EditIcon /> : <AddIcon />)}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {loading ? 'Saving...' : (editMode ? 'Update Vehicle' : 'Add Vehicle')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderBindDeviceDialog = () => (
    <Dialog
      open={openBindDevice}
      onClose={() => setOpenBindDevice(false)}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>Bind GPS Device</Typography>
          {isMobile && (
            <IconButton onClick={() => setOpenBindDevice(false)} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedVehicle && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Binding device to: <strong>{selectedVehicle.plateNumber}</strong>
              <br />
              <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                VIN: {selectedVehicle.vin}
              </Typography>
            </Alert>

            {/* ✅ SMART SUGGESTION - Show if matching device found */}
            {suggestedDevice && (
              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>✓ Matching Device Found!</strong>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Device with VIN <strong>{suggestedDevice.vin}</strong> matches this vehicle.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => handleBindDevice(selectedVehicle.id, suggestedDevice.id)}
                  disabled={loading}
                  startIcon={<LinkIcon />}
                  sx={{ mt: 1 }}
                >
                  Quick Bind This Device
                </Button>
              </Alert>
            )}

            <Typography variant="h6" gutterBottom>
              {suggestedDevice ? 'Or choose another device:' : 'Available Devices:'}
            </Typography>
            <List>
              {unboundDevices.map((device) => {
                const isRecommended = suggestedDevice?.id === device.id;

                return (
                  <ListItem
                    key={device.id}
                    sx={{
                      bgcolor: isRecommended ? 'success.lighter' : 'transparent',
                      border: isRecommended ? '2px solid' : 'none',
                      borderColor: isRecommended ? 'success.main' : 'transparent',
                      borderRadius: 1,
                      mb: 1
                    }}
                    secondaryAction={
                      <Button
                        variant={isRecommended ? 'contained' : 'outlined'}
                        color={isRecommended ? 'success' : 'primary'}
                        size="small"
                        onClick={() => handleBindDevice(selectedVehicle.id, device.id)}
                        disabled={loading || device.status === 'bound'}
                        startIcon={isRecommended ? <CheckIcon /> : null}
                      >
                        {loading ? 'Binding...' : isRecommended ? 'Recommended' : 'Bind'}
                      </Button>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{
                        bgcolor: device.status === 'bound' ? 'error.main' :
                          isRecommended ? 'success.main' : 'primary.main'
                      }}>
                        <SecurityIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            GPS Device {device.imei} {/* Last 6 digits of IMEI */}
                          </Typography>
                          {isRecommended && (
                            <Chip
                              size="small"
                              label="VIN Match"
                              color="success"
                              icon={<CheckIcon />}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            IMEI: {device.imei}
                          </Typography>
                          {device.vin && (
                            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              VIN: {device.vin} {device.vin === selectedVehicle.vin && '✓'}
                            </Typography>
                          )}
                          {device.status === 'bound' && device.boundVehicleId && (
                            <Typography variant="body2" color="error" component="div">
                              Already bound to: {vehicles.find(v => v.id === device.boundVehicleId)?.plateNumber || 'Unknown Vehicle'}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {unboundDevices.length === 0 && (
              <Alert severity="warning">
                No unbound devices available. All devices are currently bound to vehicles or inactive.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenBindDevice(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  // Unbind device dialog with reason selection
  const renderUnbindDeviceDialog = () => (
    <Dialog
      open={openUnbindDialog}
      onClose={() => {
        setOpenUnbindDialog(false);
        setSelectedUnbindVehicle(null);
        setUnbindReason('manual_unbind');
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>Unbind GPS Device</Typography>
          {isMobile && (
            <IconButton onClick={() => {
              setOpenUnbindDialog(false);
              setSelectedUnbindVehicle(null);
              setUnbindReason('manual_unbind');
            }} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedUnbindVehicle && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>⚠️ Warning:</strong> Unbinding will <strong>permanently delete</strong> this vehicle from the system.
              </Typography>
              <Typography variant="body2">
                Vehicle: <strong>{selectedUnbindVehicle.plateNumber}</strong> ({selectedUnbindVehicle.vin})
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                To add this vehicle back, you must bind it again from the Devices page.
              </Typography>
            </Alert>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Reason for Unbinding</InputLabel>
              <Select
                value={unbindReason}
                onChange={(e) => setUnbindReason(e.target.value)}
                label="Reason for Unbinding"
              >
                <MenuItem value="manual_unbind">Manual Unbind</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="replacement">Device Replacement</MenuItem>
                <MenuItem value="unauthorized_swap">Unauthorized Swap</MenuItem>
                <MenuItem value="upgrade">Device Upgrade</MenuItem>
                <MenuItem value="theft">Theft/Security</MenuItem>
                <MenuItem value="malfunction">Device Malfunction</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please select a reason for unbinding the device. This will be recorded in the system for audit purposes.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{
        flexDirection: isMobile ? 'column-reverse' : 'row',
        gap: isMobile ? 1 : 0,
        p: 2,
        '& > button': {
          width: isMobile ? '100%' : 'auto'
        }
      }}>
        <Button onClick={() => {
          setOpenUnbindDialog(false);
          setSelectedUnbindVehicle(null);
          setUnbindReason('manual_unbind');
        }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => selectedUnbindVehicle && handleUnbindDevice(selectedUnbindVehicle.id, unbindReason)}
          disabled={loading}
        >
          {loading ? 'Unbinding...' : 'Unbind Device'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderOverviewTab = () => (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Enhanced Fleet Statistics Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
        {categoryStats.map((category) => (
          <Grid item xs={6} sm={6} md={3} key={category.key}>  {/* Changed from xs={12} to xs={6} */}
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: `${category.color}.main`, width: 48, height: 48 }}>
                    {category.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div">
                      {category.vehicleCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {category.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={`${category.boundCount} Tracked`}
                        color={category.boundCount === category.vehicleCount ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {category.description}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={category.vehicleCount > 0 ? (category.boundCount / category.vehicleCount) * 100 : 0}
                  sx={{ mt: 2 }}
                  color={category.color as any}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {category.vehicleCount > 0 ? Math.round((category.boundCount / category.vehicleCount) * 100) : 0}% devices bound
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Enhanced Stats Row with Assignment Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <CarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {vehicles.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Vehicles
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {vehicles.filter(v => v.deviceBound).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Device Bound
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {vehicles.filter(v => v.assignedDepartment).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Dept Assigned
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <LocationIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {vehicles.filter(v => v.assignedRegion).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Region Assigned
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts and Notifications */}
      {(overdueMaintenance.length > 0 || upcomingMaintenance.length > 0) && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {overdueMaintenance.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <WarningIcon color="error" />
                    <Typography variant="h6" color="error.main">
                      Overdue Maintenance
                    </Typography>
                  </Stack>
                  <List dense>
                    {overdueMaintenance.slice(0, 3).map((vehicle) => (
                      <ListItem key={vehicle.id} divider>
                        <ListItemText
                          primary={`${vehicle.plateNumber} - ${vehicle.make} ${vehicle.model}`}
                          secondary={`Due: ${vehicle.nextMaintenanceDate?.toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {overdueMaintenance.length > 3 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      +{overdueMaintenance.length - 3} more vehicles need attention
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {upcomingMaintenance.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <MaintenanceIcon color="warning" />
                    <Typography variant="h6" color="warning.main">
                      Upcoming Maintenance
                    </Typography>
                  </Stack>
                  <List dense>
                    {upcomingMaintenance.slice(0, 3).map((vehicle) => (
                      <ListItem key={vehicle.id} divider>
                        <ListItemText
                          primary={`${vehicle.plateNumber} - ${vehicle.make} ${vehicle.model}`}
                          secondary={`Due: ${vehicle.nextMaintenanceDate?.toLocaleDateString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {upcomingMaintenance.length > 3 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      +{upcomingMaintenance.length - 3} more scheduled
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );

  const renderVehiclesTab = () => (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: isMobile ? 1.5 : 2, mb: isMobile ? 2 : 3, width: '100%', boxSizing: 'border-box' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">

          <TextField
            size="small"
            fullWidth={isMobile}
            placeholder="Search by plate, VIN, IMEI, make, model, or year..."  // ✅ Updated
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: isMobile ? 'auto' : 250 }}
          />

          <FormControl size="small" sx={{ minWidth: isMobile ? 'auto' : 150, width: isMobile ? '100%' : 'auto' }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="executive">Executive</MenuItem>
              <MenuItem value="staff_bus">Staff Bus</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

        </Stack>
      </Paper>

      {/* Vehicle List - Cards on Mobile, Table on Desktop */}
      {isMobile ? (
        // MOBILE: Card View
        <Stack spacing={1.5}>
          {filteredVehicles.map((vehicle) => (
            <MobileVehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              onEdit={handleEditVehicle}
              onBindDevice={(v) => {
                setSelectedVehicle(v);
                setOpenBindDevice(true);
              }}
              onUnbindDevice={openUnbindDialogForVehicle}
              getCategoryColor={getCategoryColor}
              getCategoryIcon={getCategoryIcon}
              getDepartmentName={getDepartmentName}
              getRegionName={getRegionName}
              drivers={drivers}
            />
          ))}

          {filteredVehicles.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No vehicles found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {searchQuery || selectedCategory ? 'Try adjusting your filters' : 'Add your first vehicle'}
              </Typography>
            </Box>
          )}
        </Stack>
      ) : (
        // DESKTOP: Table View
        <Paper sx={{ width: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVehicles.map((vehicle) => (
                <TableRow key={vehicle.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: getCategoryColor(vehicle.category) + '.main' }}>
                        {getCategoryIcon(vehicle.category)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {vehicle.plateNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vehicle.make} {vehicle.model} ({vehicle.year})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          VIN: {vehicle.vin}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={vehicle.category.replace('_', ' ')}
                      color={getCategoryColor(vehicle.category) as any}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={vehicle.isActive ? 'Active' : 'Inactive'}
                      color={vehicle.isActive ? 'success' : 'error'}
                    />
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Chip
                        size="small"
                        label={vehicle.deviceBound ? 'Bound' : 'Unbound'}
                        color={vehicle.deviceBound ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {vehicle.assignedDepartment ? getDepartmentName(vehicle.assignedDepartment) : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {vehicle.assignedRegion ? getRegionName(vehicle.assignedRegion) : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {(() => {
                        if (!vehicle.assignedDriver) return '-';
                        const driver = drivers.find(d => d._id === vehicle.assignedDriver);
                        return driver ? `${driver.firstName} ${driver.lastName}` : 'Assigned';
                      })()}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditVehicle(vehicle)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {vehicle.deviceBound ? (
                        <Tooltip title="Unbind Device">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openUnbindDialogForVehicle(vehicle)}
                          >
                            <UnlinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Bind Device">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedVehicle(vehicle);
                              setOpenBindDevice(true);
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredVehicles.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No vehicles found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery || selectedCategory ? 'Try adjusting your filters' : 'Click "Add Vehicle" to get started'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );

  const renderDevicesTab = () => (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">GPS Devices</Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={loadDevices} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Devices ({unboundDevices.length})
              </Typography>
              <List>
                {unboundDevices.map((device) => (
                  <ListItem key={device.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <SecurityIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle2">
                            GPS Device (IMEI: ...{device.imei.slice(-6)})
                          </Typography>
                          {device.vin && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              VIN: {device.vin}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={`Status: ${device.status}`}
                    />
                  </ListItem>
                ))}
              </List>
              {unboundDevices.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No unbound devices available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bound Devices ({devices.filter(d => d.status === 'bound').length})
              </Typography>
              <List>
                {devices.filter(d => d.status === 'bound').map((device) => (
                  <ListItem key={device.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <SecurityIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${device.manufacturer}`}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            IMEI: {device.imei}
                          </Typography>
                          <br />
                          <Typography variant="body2" color="text.secondary">
                            Bound to: {vehicles.find(v => v.id === device.boundVehicleId)?.plateNumber || 'Unknown Vehicle'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {devices.filter(d => d.status === 'bound').length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No bound devices
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMaintenanceTab = () => (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Maintenance Overview</Typography>
      </Paper>

      <Grid container spacing={3}>
        {overdueMaintenance.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <WarningIcon color="error" />
                  <Typography variant="h6" color="error.main">
                    Overdue Maintenance ({overdueMaintenance.length})
                  </Typography>
                </Stack>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Last Service</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Days Overdue</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overdueMaintenance.map((vehicle) => {
                      const daysOverdue = Math.floor((Date.now() - (vehicle.nextMaintenanceDate?.getTime() || 0)) / (1000 * 60 * 60 * 24));
                      return (
                        <TableRow key={vehicle.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">
                                {vehicle.plateNumber}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {vehicle.make} {vehicle.model}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {vehicle.lastMaintenanceDate?.toLocaleDateString() || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {vehicle.nextMaintenanceDate?.toLocaleDateString() || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${daysOverdue} days`}
                              color="error"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button size="small" variant="outlined">
                              Schedule
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {upcomingMaintenance.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <MaintenanceIcon color="warning" />
                  <Typography variant="h6" color="warning.main">
                    Upcoming Maintenance ({upcomingMaintenance.length})
                  </Typography>
                </Stack>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Last Service</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Days Until Due</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upcomingMaintenance.map((vehicle) => {
                      const daysUntil = Math.ceil((vehicle.nextMaintenanceDate?.getTime() || 0 - Date.now()) / (1000 * 60 * 60 * 24));
                      return (
                        <TableRow key={vehicle.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">
                                {vehicle.plateNumber}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {vehicle.make} {vehicle.model}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {vehicle.lastMaintenanceDate?.toLocaleDateString() || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {vehicle.nextMaintenanceDate?.toLocaleDateString() || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${daysUntil} days`}
                              color={daysUntil <= 7 ? 'error' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button size="small" variant="outlined">
                              Schedule
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {overdueMaintenance.length === 0 && upcomingMaintenance.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <MaintenanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Maintenance Alerts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All vehicles are up to date with their maintenance schedules
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{
      p: isMobile ? 1.5 : 3,
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}>
      <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" gutterBottom>
        Fleet Management
      </Typography>

      <Typography variant={isMobile ? 'caption' : 'body1'} color="text.secondary" sx={{ mb: isMobile ? 2 : 3 }}>
        Manage your vehicle fleet, GPS devices, and maintenance schedules
      </Typography>


      {/* Tab Navigation */}
      <Paper sx={{ mb: isMobile ? 2 : 3, width: '100%', overflowX: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            '& .MuiTab-root': {
              minHeight: isMobile ? 48 : 72,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              px: isMobile ? 0.5 : 2,
            }
          }}
        >
          <Tab label="Overview" icon={<SpeedIcon />} iconPosition={isMobile ? 'start' : 'top'} />
          <Tab label="Vehicles" icon={<CarIcon />} iconPosition={isMobile ? 'start' : 'top'} />
          <Tab label="Devices" icon={<SecurityIcon />} iconPosition={isMobile ? 'start' : 'top'} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && renderOverviewTab()}
      {activeTab === 1 && renderVehiclesTab()}
      {activeTab === 2 && renderDevicesTab()}

      {/* Dialogs */}
      {renderVehicleForm()}
      {renderBindDeviceDialog()}
      {renderUnbindDeviceDialog()}

      {/* Snackbars for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error"
          sx={{ width: '100%' }}
          action={
            <IconButton size="small" onClick={() => setError('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccess('')}
          severity="success"
          sx={{ width: '100%' }}
          action={
            <IconButton size="small" onClick={() => setSuccess('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FleetManagementPage;